import { useEffect, useRef } from "react";
import {
	useDispatch as useDispachRedux,
	useSelector as useSelectorRedux,
} from "react-redux";
import type { TypedUseSelectorHook } from "react-redux";

import type { RootState, AppDispatch } from "../store";

import { getStrings } from "./APIrequests";
import { updateStrings } from "./strings";

interface UseEventListenerPropsI {
	type: string;
	handler: (data: any) => void;
	withData?: boolean;
}

export const useEventListener = ({
	type,
	handler,
	withData = false,
}: UseEventListenerPropsI) => {
	const savedHandler = useRef(null);

	useEffect(() => {
		savedHandler.current = handler;
	}, [handler]);

	useEffect(() => {
		const listener = (event) =>
			savedHandler.current(withData ? { ...event.detail } : event);

		document.addEventListener(type, listener);

		return () => {
			document.removeEventListener(type, listener);
		};
	}, [type]);
};

export const useDispatch: () => AppDispatch = useDispachRedux;
export const useSelector: TypedUseSelectorHook<RootState> = useSelectorRedux;

export const useCachedStrings = () => {
	const STRINGS_CACHE_KEY = "addtocart_strings_cache";
	const STRINGS_CACHE_TIMESTAMP_KEY = "addtocart_strings_cache_timestamp";
	const CACHE_DURATION = 24 * 60 * 60 * 1000;

	useEffect(() => {
		const loadStrings = async () => {
			try {
				const cachedStrings = sessionStorage.getItem(STRINGS_CACHE_KEY);
				const cachedTimestamp = sessionStorage.getItem(
					STRINGS_CACHE_TIMESTAMP_KEY,
				);

				if (cachedStrings && cachedTimestamp) {
					const timestamp = parseInt(cachedTimestamp, 10);
					const now = Date.now();

					if (now - timestamp < CACHE_DURATION) {
						const parsedStrings = JSON.parse(cachedStrings);
						updateStrings(parsedStrings);

						return;
					}
				}

				const response = await getStrings();
				updateStrings(response);

				sessionStorage.setItem(STRINGS_CACHE_KEY, JSON.stringify(response));
				sessionStorage.setItem(
					STRINGS_CACHE_TIMESTAMP_KEY,
					Date.now().toString(),
				);
			} catch (err) {
				console.error(err);
			}
		};

		loadStrings();
	}, []);

	const clearCache = () => {
		sessionStorage.removeItem(STRINGS_CACHE_KEY);
		sessionStorage.removeItem(STRINGS_CACHE_TIMESTAMP_KEY);
	};

	return { clearCache };
};
